<template>
  <section class="subscription-page__wrapper" :key="key">
    <flash-message></flash-message>
    <template v-if="ready">
      <!--      ***                                     -->
      <!--      OTHER PROVIDER SUBSCRIPTION WARNING     -->
      <!--      ***                                     -->
    <section class="subscription-page">
      <template v-if="userSubscriptions && userSubscriptions.hasSubscriptionThroughOtherProvider && userStripeSubscription === null && !hasBypassedWarning">
        <div class="subscription-warning--gate">
          <p class="title">Warning</p>
          <p>You currently have an active subscription via {{ userSubscriptions.otherProvider }}.<br /> To manage this subscription, please go to the {{ userSubscriptions.otherProvider }} App Store.</p>
          <p>If you wish to change your subscription payment method, you can click the button below.</p>
          <p>To avoid being charged twice, you will need to cancel your app store based subscription yourself, as we are unable to do this automatically on your behalf.</p>
          <p class="text-center mt-40"><v-button @click="hasBypassedWarning = true" class="btn btn-primary">Continue anyway</v-button></p>
        </div>
      </template>
      <!--      ***                     -->
      <!--      MANUAL SUBSCRIPTION     -->
      <!--      ***                     -->
      <template v-else-if="userSubscriptions && userSubscriptions.manual !== null">
        <span v-if="userSubscriptions.hasChallengeSubscription">You currently have a complimentary subscription from joining our reading challenge. Please return once that subscription has expired or contact us for further support.</span>
        <span v-else>You have a bespoke subscription to this package. Please contact us to amend or query this subscription.</span>
      </template>
      <!--      ***                     -->
      <!--      STRIPE SUBSCRIPTION     -->
      <!--      ***                     -->
      <template v-else>
        <!-- MODALS -->
        <subscribe-modal :price="selectedPrice" :cb="subscribeWithNewCard" :currency="selectedCurrency" v-if="modal.isVisible('stripe')" @close="modal.hide('stripe')" />
        <subscribe-with-card-modal :card-id="defaultCardId" :customer="customer" :price="selectedPrice" :currency="selectedCurrency" :cb="subscribeWithExistingCard" v-if="modal.isVisible('subscribe')" @close="modal.hide('subscribe')" @swap="modal.hide('subscribe') & modal.show('stripe')" />
        <unsubscribe-modal v-if="modal.isVisible('unsubscribe')" @close="modal.hide('unsubscribe')" :cb="unsubscribe" />
        <reactivate-subscription-modal v-if="modal.isVisible('reactivate')" @close="modal.hide('reactivate')" :cb="reactivateSubscription" />
        <info-about-plan-modal v-if="modal.isVisible('info')" @close="modal.hide('info')" :title="modalInfo.title" :message="modalInfo.message" />
        <subscription-success-modal v-if="modal.isVisible('success')" @close="modal.hide('success')" :next-payment-date="userSubscriptions ? userSubscriptions.nextPaymentDate : null" :is-downgrading="isDowngrading" :cb="downloadApp" />
        <!-- /MODALS -->
        <div class="subscription-header">
          <div class="subscription-header__title">{{ subscriptionPackage.title }}</div>
          <template v-if="subscriptionPackage.hasSubscription && userStripeSubscription !== null">
            <div class="subscription-header__status sm-none">
              <div class="subscription-header__status--inner">
                subscribed
              </div>
            </div>
          </template>
        </div>
        <div class="subscription-description">{{ subscriptionPackage.description }}</div>
        <!--      ***                               -->
        <!--      ARCHIVED STRIPE SUBSCRIPTION      -->
        <!--      ***                               -->
        <template v-if="userStripeSubscription && userStripeSubscription.is_archived && userStripeSubscription.renewal_status !== 'cancelled'">
          <div class="promocode__summary text-center" :style="{'text-transform': 'none'}">
            This plan is no longer available. Please cancel your subscription if you wish to see our other plans otherwise you will continue to have access to the Fonetti library as long as this subscription is renewed.
          </div>
          <div class="subscription-badges">
            <subscription-badge
                :price="subscribedPrice"
                :currency="CURRENCIES[CURRENCIES_SHORTNAME_TO_COUNTRY_ID[userStripeSubscription.payment_currency]]"
                :show-variant="false"
            />
          </div>
          <div class="subscription-footer">
            <div class="subscription-footer__cancel">
              <a @click="modal.show('unsubscribe')">cancel subscription</a>
            </div>
          </div>
        </template>
        <!--      ***                               -->
        <!--      NORMAL STRIPE SUBSCRIPTION        -->
        <!--      ***                               -->
        <template v-else>
          <promocode-form v-if="shouldDisplayPromocodeForm" v-model="previewCode" @validated="callPreviewPromocode" />
          <div class="promocode__summary" v-if="promocode && promocode.isRunning">
            <p v-html="promocode.prettyDescription"></p> <v-button v-if="userStripeSubscription !== null" class="btn btn-primary" group="promocode" @click="callApplyPromocode">apply</v-button> <v-button class="btn btn-primary" group="promocode" @click="callClearPromocode">cancel</v-button>
          </div>
          <template v-if="userStripeSubscription === null">
            <SegmentedControls
               name="selected-currency"
               :controls="currencyControls"
               @update-value="handleChangeCurrency($event)"
            />
          </template>
          <div class="subscription-badges">
            <template>
              <template v-for="variant in subscriptionPackage.variantsToDisplay">
                <template v-for="price in variant.prices">
                  <subscription-badge
                      :key="price.id"
                      v-if="price.currency === selectedCurrency.short_name"
                      :currency="selectedCurrency"
                      :price="price"
                      @selected="selectPrice($event, { price, variant })"
                      @click="showInfoModal(price)"
                      :is-disabled="badgeDisabled(price)"
                  />
                </template>
              </template>
            </template>
          </div>
          <div class="subscription-footer">
            <template v-if="userStripeSubscription && userStripeSubscription.renewal_status !== 'cancelled'">
              <div class="subscription-footer__info" v-html="nextPaymentText"></div>
              <div class="subscription-footer__cancel" v-if="userStripeSubscription && userStripeSubscription.renewal_status !== 'cancelled'">
                <a @click="modal.show('unsubscribe')">cancel subscription</a>
              </div>
            </template>
            <template v-else-if="userStripeSubscription && userStripeSubscription.renewal_status === 'cancelled'">
              <div class="mt-40">
                <v-button @click="modal.show('reactivate')" class="btn btn-primary">reactivate subscription</v-button>
              </div>
            </template>
            <div class="subscription-footer__info" v-html="currentDiscountText"></div>
            <div class="subscription-footer__promo" v-if="promocode && priceSelected">
              {{ promocode.longerPrettyDescription }}
            </div>
            <div class="subscription-footer__subscribe" v-if="selectedPrice">
              <p v-if="userStripeSubscription !== null">Clicking on the button below will immediately change your plan.</p>
              <v-button class="btn btn-primary" group="subscribe" @click="subscribe">
                <template v-if="userStripeSubscription !== null">
                change plan
                </template>
                <template v-else>
                subscribe
                </template>
              </v-button>
            </div>
          </div>
        </template>
      </template>
      <template v-if="userSubscriptions && userSubscriptions.hasSubscriptionThroughOtherProvider && userStripeSubscription">
        <div class="subscription-warning--alert">
          <fa-layers class="exclamation-circle" style="font-size:1.5rem; vertical-align: bottom;">
            <fa icon="circle" style="color: #7f0d0d;"></fa>
            <fa icon="exclamation" class="exclamation-circle__exclamation"></fa>
          </fa-layers>
          Don't forget you have another active subscription with {{ userSubscriptions.otherProvider }}. You should cancel this to avoid being charged twice. We are unable to cancel app store subscriptions on your behalf.
        </div>
      </template>
    </section>
    </template>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import _ from 'lodash';
import PromocodeForm from '@/components/forms/PromocodeForm';
import SubscriptionBadge from '@/components/SubscriptionBadge';
import ModalJS from '@/components/classes/Modal';
import PromocodeMixin from '@/components/mixins/PromocodeMixin';
import Button from '@/components/utils/Button';
import SubscriptionMixin from '@/components/mixins/SubscriptionMixin';
import CustomerMixin from '@/components/mixins/CustomerMixin';
import HelpersMixin from '@/components/mixins/HelpersMixin';
import UserMixin from '@/components/mixins/UserMixin';
import StripeMixin from '@/components/mixins/StripeMixin';
import SegmentedControls from '@/components/SegmentedControls';
import UserSubscriptions from '@/components/classes/UserSubscriptions';
import SubscriptionPackage from '@/components/classes/SubscriptionPackage';
import SubscribeModal from '@/components/partials/subscription/SubscribeModal';
import SubscribeWithCardModal from '@/components/partials/subscription/SubscribeWithCardModal';
import {
  CURRENCIES, CURRENCIES_SHORTNAME_TO_COUNTRY_ID, DURATIONS, PACKAGE_ID,
} from '@/constants';
import Promocode from '@/components/classes/Promocode';
import UnsubscribeModal from '@/components/partials/subscription/UnsubscribeModal';
import SubscriptionSuccessModal from '@/components/partials/subscription/SubscriptionSuccessModal';
import ReactivateSubscriptionModal from '@/components/partials/subscription/ReactivateSubscriptionModal';
import InfoAboutPlanModal from '@/components/partials/subscription/InfoAboutPlanModal';

export default {
  name: 'subscription',
  mixins: [PromocodeMixin, SubscriptionMixin, CustomerMixin, HelpersMixin, UserMixin, StripeMixin],
  components: {
    InfoAboutPlanModal,
    ReactivateSubscriptionModal,
    SubscriptionSuccessModal,
    UnsubscribeModal,
    PromocodeForm,
    SubscriptionBadge,
    'v-button': Button,
    SegmentedControls,
    SubscribeModal,
    SubscribeWithCardModal,
  },
  data() {
    return {
      subscriptionPackage: null,
      previewCode: '',
      promocode: null,
      customer: null,
      modal: new ModalJS({
        unsubscribe: false,
        subscribe: false,
        stripe: false,
        success: false,
        reactivate: false,
        info: false,
        cancel: false,
      }),
      subscribedPrice: null,
      priceSelected: false,
      selectedPrice: null,
      selectedVariant: null,
      cardId: null,
      nextModalToOpen: null,
      isDowngrading: false,
      hasActiveDiscount: false,
      ready: false,
      key: 1,
      modalInfo: {},
      subscriptionDurationToShow: DURATIONS.DEFAULT,
      CURRENCIES,
      CURRENCIES_SHORTNAME_TO_COUNTRY_ID,
      selectedCurrency: CURRENCIES.GB,
      disabledDuration: {
        month: false,
        year: false,
      },
      disabledCurrencies: {},
      currencyControls: [],
      userSubscriptions: null,
      userStripeSubscription: null,
      hasBypassedWarning: false,
    };
  },
  created() {
    this.init();
  },
  mounted() {
    // set default currency
    let selectedCurrency = null;
    const currencyKeys = Object.keys(CURRENCIES);
    // if the user has a country set:
    // set selected currency into that country
    if (this.user.country !== 'GB' && this.user.country !== null) {
      selectedCurrency = CURRENCIES[this.user.country];
    }

    // if the user has a current subscription:
    // set selected currency into the selected plan's currency
    for (let i = 0; i < currencyKeys.length; i++) {
      if (this.userStripeSubscription) {
        if (CURRENCIES[currencyKeys[i]].short_name === this.userStripeSubscription.payment_currency) {
          selectedCurrency = CURRENCIES[currencyKeys[i]];
        }
      }
      this.currencyControls.push({
        value: currencyKeys[i],
        text: `${CURRENCIES[currencyKeys[i]].short_name} ${CURRENCIES[currencyKeys[i]].symbol}`,
        isActive: selectedCurrency !== null ? selectedCurrency.symbol === CURRENCIES[currencyKeys[i]].symbol : currencyKeys[i] === 'GB',
        disabled: false,
        disabled_message: `${CURRENCIES[currencyKeys[i]].short_name} is not available with the current promotion code`,
        currency: CURRENCIES[currencyKeys[i]].short_name,
      });
    }

    // update selected currency state;
    if (selectedCurrency) {
      this.selectedCurrency = selectedCurrency;
    }

    document.body.classList.remove('modal-open');
  },
  beforeDestroy() {
    this.$bus.$off('promocode-cleared');
  },
  methods: {
    hideAllModals() {
      this.modal.hideAll();
    },
    init(cb = null) {
      this.getUser(PACKAGE_ID)
        .then((user) => {
          this.userSubscriptions = new UserSubscriptions(user.subscriptions);
          this.userStripeSubscription = this.userSubscriptions.stripe;
          return this.getCustomer();
        })
        .then((customer) => {
          this.customer = customer;
          this.cardId = this.defaultCardId;
        })
        .catch((error) => {
          const extractedMessage = this.extractMessage(error, 'error');
          if (extractedMessage !== 'Customer not found') {
            this.flashMessage(error, 'error');
          }
        })
        .finally(() => {
          this.prepareSubscriptionPackage(cb);
        });
    },
    downloadApp() {
      window.open('https://apps.apple.com/app/fonetti/id1332609392', '_blank');
    },
    prepareSubscriptionPackage(cb = null) {
      this.getSubscriptionPackage(1, this.subscriptionParams())
        .then((subscriptionPackage) => {
          this.subscriptionPackage = SubscriptionPackage.fromJson(subscriptionPackage);
          this.subscribedPrice = this.subscriptionPackage.getSubscribedPrice();
          this.ready = true;
          if (cb !== null && typeof cb === 'function') {
            cb();
          }
        })
        .catch((error) => {
          this.flashMessage(error, 'error');
        });
    },
    callPreviewPromocode() {
      this.promocodeLoading = true;
      this.buttonGroupIsLoading('promocode', true);
      this.previewPromocode(this.previewCode, 'subscription')
        .then((promocode) => {
          this.resetSelection();
          this.promocode = Promocode.fromJson(promocode);
          this.flashMessage();
        })
        .catch((error) => {
          this.promocode = null;
          this.flashMessage(error, 'error');
        })
        .finally(() => {
          this.prepareSubscriptionPackage(() => {
            this.promocodeLoading = false;
            this.buttonGroupIsLoading('promocode', false);
          });
        });
    },
    callClearPromocode() {
      const query = { ...this.$route.query };
      delete query.promo;
      this.$router.replace({ query })
        .then(() => {})
        .catch(() => {});
      this.buttonGroupIsLoading('promocode', true);
      this.clearPromocode();
      this.resetSelection();
      this.prepareSubscriptionPackage(() => {
        this.buttonGroupIsLoading('promocode', false);
      });
    },
    callApplyPromocode() {
      this.buttonGroupIsLoading('promocode', true);
      this.applyPromocode(this.promocode.code, this.userStripeSubscription)
        .then(() => {
          this.flashMessage('The promocode has been applied', 'success');
          this.init();
        })
        .catch((error) => {
          this.flashMessage(error, 'error');
        })
        .finally(() => {
          this.promocode = null;
          this.buttonGroupIsLoading('promocode', false);
        });
    },
    selectPrice(payload, { price, variant }) {
      if (payload === true) {
        this.priceSelected = payload;
        this.selectedPrice = price;
        this.selectedVariant = variant;
      } else {
        this.resetSelection();
      }
    },
    resetSelection() {
      this.priceSelected = false;
      this.selectedPrice = null;
      this.selectedVariant = null;
      this.$bus.$emit('price-cleared');
    },
    subscribe() {
      let modalToOpen = 'stripe';
      if (!_.isEmpty(this.customer) && !_.isEmpty(this.customer.cards)) {
        modalToOpen = 'subscribe';
      }

      if (this.subscriptionPackage.hasSubscription && this.userStripeSubscription !== null) {
        this.updateSubscription();
        return;
      }

      this.modal.show(modalToOpen);
    },
    subscribeWithNewCard({ card }) {
      window.scrollTo(0, 0);
      this.stripe.createPaymentMethod({
        type: 'card',
        card,
      })
        .then((result) => {
          if (result.error) {
            this.buttonGroupIsLoading('all', false);
            this.flashMessage(result.error.message, 'error', 'stripe');
          } else {
            const code = _.get(this.selectedPrice, 'promocode_data.applicable_promocode.code', null);
            const params = {
              payment_data: {
                provider_ref: this.selectedPrice.payment_provider_ref,
                card_id: result.paymentMethod.id,
                email: this.$store.state.user.email,
                code,
              },
            };
            this.makePayment(params, result.paymentMethod.id);
          }
        });
    },
    subscribeWithExistingCard(cardId) {
      window.scrollTo(0, 0);
      const code = _.get(this.selectedPrice, 'promocode_data.applicable_promocode.code', null);
      const params = {
        payment_data: {
          provider_ref: this.selectedPrice.payment_provider_ref,
          card_id: cardId,
          email: this.$store.state.user.email,
          code,
        },
      };
      this.makePayment(params, cardId);
    },
    cleanUpAfterPaymentAttempt() {
      this.resetSelection();
      this.buttonGroupIsLoading('all', false);
      this.clearPromocode();
      this.$store.commit('RESET_PROMOCODE');
    },
    paymentMade() {
      this.hideAllModals();
      this.init(() => this.modal.show('success'));
    },
    unsubscribe(immediate = false) {
      this.buttonGroupIsLoading('subscribe', true);
      this.$api.delete('/subscriptions/stripe', { data: { payment_provider_subscription_id: this.userStripeSubscription.payment_provider_subscription_id, immediate } })
        .then(() => {
          this.init(() => {
            if (immediate) {
              this.key++;
            }
          });
        })
        .catch((error) => {
          this.flashMessage(error, 'error');
        })
        .finally(() => {
          this.modal.hideAll();
          this.buttonGroupIsLoading('subscribe', false);
          this.$forceUpdate();
        });
    },
    subscriptionParams() {
      const params = {};
      params.code = this.promocode === null ? this.queryCode : this.promocode.code;
      params.customer = _.get(this.customer, 'id', null);
      // params.subscription = this.currentPaymentProviderSubscriptionId;
      return params;
    },
    badgeDisabled(price) {
      if (this.userStripeSubscription && this.userStripeSubscription.price_id === price.id) {
        return false;
      }

      if (this.promocode && this.promocode.isRunning && !SubscriptionPackage.isPriceDiscountable(price, this.promocode)) {
        return true;
      }

      // previewing promocode + attempting to change plan = not allowed
      if (this.userStripeSubscription !== null && this.promocode !== null && this.promocode.isRunning && !price.has_subscription) {
        return true;
      }

      // can't change plan if subscription is in cancellation period
      // can't choose monthly if already on a yearly plan (need to cancel first)
      if (this.userStripeSubscription && (this.userStripeSubscription.renewal_status === 'cancelled' || this.userStripeSubscription.renewal_duration_unit === 'year')) {
        return true;
      }

      return false;
    },
    badgeDisabledReasonMessage(price) {
      if (this.userStripeSubscription && this.userStripeSubscription.renewal_duration_unit === 'year') {
        return {
          title: 'Plan change unavailable',
          message: 'If you wish to change your plan to monthly, please, first cancel your yearly plan then resubscribe.',
        };
      }
      if (this.userStripeSubscription && this.userStripeSubscription.renewal_status === 'cancelled') {
        return {
          title: 'Plan change unavailable',
          message: 'We cannot change your plan whilst you are in your cancellation period. If you wish to stay subscribed, please reactivate your subscription and then you can change plans if you wish.',
        };
      }
      if (this.promocode && this.promocode.isRunning) {
        if (this.userStripeSubscription !== null && !price.has_subscription) {
          return {
            title: 'Plan change unavailable',
            message: 'We are unable to support plan changing whilst applying a promotion code.',
          };
        }
        if (price.promocode_data.is_discountable === false) {
          return {
            title: 'Plan unavailable',
            message: 'This plan is not available with the current promotion code. Remove the applied promotion code to select this plan.',
          };
        }
      }

      return null;
    },
    showInfoModal(price) {
      this.modalInfo = this.badgeDisabledReasonMessage(price);
      if (this.modalInfo !== null) {
        this.modal.show('info');
      }
    },
    handleChangeCurrency(e) {
      for (let i = 0; i < this.currencyControls.length; i++) {
        this.currencyControls[i].isActive = this.currencyControls[i].currency === CURRENCIES[e].short_name;
      }
      this.selectedCurrency = CURRENCIES[e];
      this.resetSelection();
    },
  },
  computed: {
    ...mapState(['queryCode', 'user', 'stripe']),
    ...mapGetters(['userReadersCount']),
    shouldDisplayPromocodeForm() {
      if (this.promocode && this.promocode.isRunning) {
        return false;
      }

      if (this.userStripeSubscription) {
        if (this.userStripeSubscription.renewal_status === 'cancelled') {
          return false;
        }
      }

      if (this.userStripeSubscription && UserSubscriptions.isDiscountActive(this.userStripeSubscription)) {
        return false;
      }

      return true;
    },
    nextPaymentText() {
      if (this.userStripeSubscription === null) {
        return null;
      }

      const date = this.userSubscriptions.nextPaymentDate;
      const amount = this.userSubscriptions.nextPaymentAmount;
      return `Your next payment of <strong>${this.selectedCurrency.symbol}${amount}</strong> is estimated to be due on the ${date}.`;
    },
    currentDiscountText() {
      const discount = this.userSubscriptions.currentDiscount;
      if (discount === null) {
        return null;
      }

      return `Your next month will use the ${discount.name} coupon.`;
    },
  },
  watch: {
    ready(newVal) {
      if (newVal === true) {
        if (this.queryCode !== '' && this.queryCode !== null) {
          this.previewCode = this.queryCode;
          this.callPreviewPromocode();
        }
      }
    },
  },
};
</script>
